import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Save, X, AlertTriangle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH = { withCredentials: true };
const EVIDENCE = ["FOUNDATIONAL", "EARLY TRANSLATIONAL", "PRECLINICAL", "LIMITED / EMERGING", "POORLY CHARACTERIZED"];
const TEXT_FIELDS = [
  ["name", "Name"], ["catalog", "Catalog name"], ["productId", "Product ID (slug)"],
  ["classification", "Classification"], ["sequence", "Sequence"], ["formula", "Formula"],
  ["mw", "Molecular weight"], ["cas", "CAS"], ["pubchem", "PubChem"],
];
const AREA_FIELDS = [["aliases", "Aliases"], ["areas", "Research areas"], ["targets", "Targets"], ["keywords", "Keywords"]];
const LONG_FIELDS = [["overview", "Molecular overview"], ["mechanism", "Mechanistic background"], ["studies", "What researchers study"], ["limitations", "Research limitations"], ["verifyNote", "Verification note"]];

export default function AdminResearch() {
  const auth = AUTH;
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null); // entry object being edited
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/research`, AUTH);
      setEntries(data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { toast.error("Could not load research entries"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ slug: "", name: "", catalog: "", productId: "", classification: "", evidence: "LIMITED / EMERGING", overview: "", mechanism: "", studies: "", limitations: "", aliases: [], areas: [], targets: [], keywords: [], verify: false, identityVerified: false, active: true, _isNew: true });

  const setField = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const setArr = (k, v) => setEditing((p) => ({ ...p, [k]: v.split(",").map((s) => s.trim()).filter(Boolean) }));

  const save = async () => {
    if (!editing.slug.trim()) { toast.error("Slug is required"); return; }
    if (!editing.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const body = { ...editing }; delete body._isNew;
      if (editing._isNew) await axios.post(`${API}/admin/research`, body, auth);
      else await axios.put(`${API}/admin/research/${editing.slug}`, body, auth);
      toast.success(`Saved “${editing.name}”`);
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save entry");
    } finally { setSaving(false); }
  };

  const remove = async (slug, name) => {
    if (!window.confirm(`Delete research entry “${name}”? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/admin/research/${slug}`, auth); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  if (editing) {
    return (
      <div className="border border-zinc-800 bg-[#0C0C0C] p-6" data-testid="admin-research-editor">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl font-light">{editing._isNew ? "New research entry" : `Editing: ${editing.name}`}</h3>
          <button onClick={() => setEditing(null)} data-testid="research-cancel" className="text-[#71717A] hover:text-white"><X size={20} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Slug (URL id)" value={editing.slug} onChange={(v) => setField("slug", v)} disabled={!editing._isNew} testid="research-slug" />
          {TEXT_FIELDS.map(([k, label]) => (
            <Field key={k} label={label} value={editing[k] || ""} onChange={(v) => setField(k, v)} testid={`research-${k}`} />
          ))}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Evidence level</label>
            <select value={editing.evidence} onChange={(e) => setField("evidence", e.target.value)} data-testid="research-evidence"
              className="w-full bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] outline-none px-3 py-2.5 font-mono text-sm text-white">
              {EVIDENCE.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          {AREA_FIELDS.map(([k, label]) => (
            <Field key={k} label={`${label} (comma-separated)`} value={(editing[k] || []).join(", ")} onChange={(v) => setArr(k, v)} testid={`research-${k}`} />
          ))}
        </div>
        <div className="grid gap-4 mt-4">
          {LONG_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">{label}</label>
              <textarea rows={2} value={editing[k] || ""} onChange={(e) => setField(k, e.target.value)} data-testid={`research-${k}`}
                className="w-full bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] outline-none px-3 py-2.5 text-sm text-[#E8E8E3] resize-none" />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-6 items-center mt-5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!editing.verify} onChange={(e) => setField("verify", e.target.checked)} data-testid="research-verify" className="accent-[#D4AF37]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA]">Show verification banner</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!editing.identityVerified} onChange={(e) => setField("identityVerified", e.target.checked)} data-testid="research-identity-verified" className="accent-emerald-500" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA]">Identity verified (green badge)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={editing.active !== false} onChange={(e) => setField("active", e.target.checked)} data-testid="research-active" className="accent-[#D4AF37]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA]">Active (visible on site)</span>
          </label>
        </div>
        <p className="text-[#52525B] text-xs mt-3 max-w-xl leading-relaxed">Only enable <span className="text-emerald-400/80">Identity verified</span> for compounds whose chemical identity is independently confirmed (authoritative CAS / characterization). It surfaces a green &ldquo;Verified Identity&rdquo; badge on the catalog and product page.</p>
        <button onClick={save} disabled={saving} data-testid="research-save"
          className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-black px-7 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : "Save entry"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-research">
      <div className="flex items-center justify-between mb-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#52525B]">{entries.length} research entries</p>
        <button onClick={startNew} data-testid="research-new-btn" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-[#C5A028] transition-colors">
          <Plus size={13} /> New entry
        </button>
      </div>
      <div className="border border-zinc-800 divide-y divide-zinc-800">
        {entries.map((e) => (
          <div key={e.slug} className="flex items-center justify-between gap-4 p-4" data-testid={`research-row-${e.slug}`}>
            <div className="min-w-0">
              <p className="font-serif text-lg truncate">{e.name}
                {e.active === false && <span className="ml-2 font-mono text-[9px] uppercase tracking-wide text-red-400 border border-red-400/40 px-1.5 py-0.5">Hidden</span>}
                {e.identityVerified && <ShieldCheck size={13} className="inline ml-2 text-emerald-400" />}
                {e.verify && <AlertTriangle size={13} className="inline ml-2 text-amber-400" />}
              </p>
              <p className="font-mono text-[10px] text-[#71717A] mt-0.5">{e.slug} · {e.evidence}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing({ ...e })} data-testid={`research-edit-${e.slug}`}
                className="flex items-center gap-1.5 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><Pencil size={12} /> Edit</button>
              <button onClick={() => remove(e.slug, e.name)} data-testid={`research-delete-${e.slug}`}
                className="flex items-center border border-zinc-700 px-3 py-2 text-[#71717A] hover:border-red-500 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-zinc-800 focus:border-[#D4AF37] outline-none px-3 py-2.5 font-mono text-sm text-white disabled:opacity-50" />
    </div>
  );
}
